<template>
  <div class="flex flex-col gap-4">
    <TaskGroupSection
      v-for="group in groups"
      :key="groupKeyOf(group)"
      :group="group"
      :show-header="showGroupHeaders"
      :selection-state="groupSelectionState(group)"
      @toggle-select-group="selection.toggleGroup(selectableIdsOf(group))"
      @toggle-collapse="$emit('toggle-collapse', group)"
      @load-more="$emit('load-more', group)"
    >
      <div class="flex flex-row gap-x-4">
        <div
          v-for="column in columnsOf(group)"
          :key="column.id"
          data-test="status-column"
          class="flex min-w-0 flex-1 flex-col gap-2"
        >
          <div
            :data-tint="column.color"
            class="relative flex h-8 items-center justify-center rounded-t border-b-4 border-primary bg-primary-50 text-center text-primary"
          >
            <h1 class="text-base font-semibold capitalize-first">
              {{ $t('enums.task.status.' + column.id) }}
            </h1>
            <span
              data-test="status-count"
              class="absolute top-1 right-1 rounded-lg bg-primary px-1.5 text-xs text-contrast-primary"
            >
              {{ column.count }}
            </span>
          </div>
          <div
            data-test="drop-zone"
            :data-tint="column.color"
            class="flex items-center justify-center rounded-lg border-2 border-dashed py-3 text-xl font-bold uppercase"
            @dragenter="(event) => event.target.classList.add('bg-primary/50')"
            @dragleave="
              (event) => event.target.classList.remove('bg-primary/50')
            "
            @dragover.prevent
            @drop="onDrop($event, group, column.id)"
          >
            {{ $t('global.drop') }}
          </div>
          <div class="flex flex-col gap-2">
            <TaskDossierCard
              v-for="task in column.tasks"
              :key="task.id"
              :task="task"
              :is-item-task="true"
              :draggable="true"
              :status-click-cycle="statusClickCycle"
              :display-fields="settings.fields"
              :selectable="selection !== null"
              :selected="selection?.isSelected(task.id) ?? false"
              @toggle-select="selection.toggle(task.id)"
              @is-dragging="onDragging($event, group)"
              @refresh-list="$emit('refresh-list')"
              @task-deleted="$emit('task-deleted', $event)"
              @task-status-updated="$emit('task-status-updated', $event)"
            />
          </div>
        </div>
      </div>
      <template #loading-more>
        <div class="flex flex-row gap-x-4">
          <BaseSkeleton :count="visibleStatusList.length">
            <div class="min-w-0 flex-1">
              <TaskCardSkeleton />
            </div>
          </BaseSkeleton>
        </div>
      </template>
    </TaskGroupSection>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import BaseSkeleton from '@c/BaseSkeleton.vue'
import TaskGroupSection from '../components/TaskGroupSection.vue'
import TaskCardSkeleton from '../components/TaskCardSkeleton.vue'
import { groupCollapseKey } from '../utils/taskDashboardSettings'
import TaskDossierCard from '@/views/dossier/form/subpages/taskDossier/components/taskDossierList/components/TaskDossierCard.vue'

export default {
  name: 'TaskBoardView',
  components: {
    BaseSkeleton,
    TaskGroupSection,
    TaskCardSkeleton,
    TaskDossierCard
  },
  props: {
    selection: { type: Object, required: false, default: null },
    groups: { type: Array, required: true },
    settings: { type: Object, required: true },
    showGroupHeaders: { type: Boolean, required: true },
    statusClickCycle: { type: Boolean, required: false, default: true },
    /** Server-side count per status over every task matching the filters (`meta.status_counts`). */
    statusCounts: { type: Object, required: false, default: () => ({}) }
  },
  emits: [
    'toggle-collapse',
    'load-more',
    'task-status-updated',
    'task-deleted',
    'refresh-list'
  ],
  data() {
    return { isDragging: false, draggingFromGroupKey: null }
  },
  computed: {
    ...mapGetters({
      task_statuses: 'metadata/getMetadataTaskStatuses',
      dataTransferObject: 'taskDossier/getDataTransferObject'
    }),
    visibleStatusList() {
      const visible = this.settings.visibleStatuses ?? []
      return visible.length === 0 || this.isDragging
        ? this.task_statuses
        : this.task_statuses.filter((status) => visible.includes(status.id))
    }
  },
  methods: {
    /** null hides the select-all: nothing to select while the group is not loaded. */
    groupSelectionState(group) {
      const ids = this.selectableIdsOf(group)
      return this.selection && ids.length > 0
        ? this.selection.groupState(ids)
        : null
    },
    /** The tasks a group's select-all covers: those shown in its visible columns. */
    selectableIdsOf(group) {
      return this.columnsOf(group).flatMap((column) =>
        column.tasks.map((task) => task.id)
      )
    },
    ...mapActions({
      processUpdateDossierTaskStatusAction: 'dossier/updateDossierTaskStatus'
    }),
    groupKeyOf(group) {
      return groupCollapseKey(group.key)
    },
    onDragging(dragging, group) {
      this.isDragging = dragging
      this.draggingFromGroupKey = dragging ? this.groupKeyOf(group) : null
    },
    /**
     * The ungrouped board shows the server count of every status (its single group only
     * holds the loaded page); grouped boards show the loaded tasks of each column.
     */
    columnsOf(group) {
      return this.visibleStatusList.map((status) => {
        const tasks = group.task_dossiers.filter(
          (task) => task.status === status.id
        )

        return {
          id: status.id,
          color: status.bg_color,
          tasks,
          count: this.showGroupHeaders
            ? tasks.length
            : (this.statusCounts[status.id] ?? 0)
        }
      })
    },
    onDrop(event, group, newStatus) {
      event.preventDefault()
      event.currentTarget.classList.remove('bg-primary/50')
      const draggedTask = this.dataTransferObject?.task
      const sourceGroupKey = this.draggingFromGroupKey
      this.isDragging = false
      this.draggingFromGroupKey = null
      if (
        !draggedTask ||
        sourceGroupKey !== this.groupKeyOf(group) ||
        !group.task_dossiers.some((task) => task.id === draggedTask.id)
      ) {
        return
      }
      this.$emit('task-status-updated', { taskId: draggedTask.id, newStatus })
      this.processUpdateDossierTaskStatusAction({
        id: draggedTask.id,
        new_status: newStatus
      })
        .then(() => {
          this.$h.toastSuccess(
            this.$t('toasts.updated_f', { var: this.$t('attributes.task') })
          )
        })
        .catch(() => {
          this.$h.toastDanger(this.$t('toasts.something_went_wrong'))
          this.$emit('refresh-list')
        })
    }
  }
}
</script>
